import React from 'react'
import { Modal } from '@/components/ui'
import { useTranslation } from '@/contexts/TranslationContext'
import type { FinancialEntry, RecurrenceRules } from '@/lib/services/billing'

export type RecurrencePeriodicity = 'daily' | 'weekly' | 'monthly' | 'yearly' | 'installment'

interface Props {
  isOpen: boolean
  entry: FinancialEntry | null
  periodicity: RecurrencePeriodicity
  setPeriodicity: (p: RecurrencePeriodicity) => void
  monthlyTargetDay: number | null
  setMonthlyTargetDay: (v: number | null) => void
  monthlyPreviewDates: string[]
  weeklyDow: number
  setWeeklyDow: (d: number) => void
  installmentCurrent: number
  setInstallmentCurrent: (v: number) => void
  installmentTotal: number
  setInstallmentTotal: (v: number) => void
  showAdvanced: boolean
  setShowAdvanced: (v: boolean) => void
  weekendAdjust: 'none' | 'next' | 'prev'
  setWeekendAdjust: (v: 'none' | 'next' | 'prev') => void
  weekdayLock: boolean
  setWeekdayLock: (v: boolean) => void
  weekdayLockDay: number
  setWeekdayLockDay: (d: number) => void
  dailyWeekdays: number[]
  setDailyWeekdays: (days: number[]) => void
  endDate: string | null
  setEndDate: (v: string | null) => void
  submitting: boolean
  onClose: () => void
  onConfirm: () => void
  isMobile?: boolean
}

export default function RecurrenceModal({ isOpen, entry, periodicity, setPeriodicity, monthlyTargetDay, setMonthlyTargetDay, monthlyPreviewDates, weeklyDow, setWeeklyDow, installmentCurrent, setInstallmentCurrent, installmentTotal, setInstallmentTotal, showAdvanced, setShowAdvanced, weekendAdjust, setWeekendAdjust, weekdayLock, setWeekdayLock, weekdayLockDay, setWeekdayLockDay, dailyWeekdays, setDailyWeekdays, endDate, setEndDate, submitting, onClose, onConfirm, isMobile = false }: Props) {
  const { t, locale } = useTranslation()
  const te = (k: string, p?: Record<string, string>) => t(`billing.entries.${k}`, p)
  if (!entry) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={te('recurrence.modal.title')}
      size="sm"
      mobileTopAligned={false}
      headerLeft={isMobile ? (
        <button
          onClick={() => !submitting && onClose()}
          disabled={submitting}
          className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
          aria-label={t('actions.cancel') || 'Cancelar'}
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      ) : undefined}
      headerRight={(
        <div className="flex items-center gap-3">
          <button
            onClick={onConfirm}
            disabled={submitting}
            className="text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label="Confirmar"
          >
            {submitting ? (
              <svg className="animate-spin w-6 h-6" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            onClick={() => !submitting && onClose()}
            disabled={submitting}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors"
            aria-label={t('actions.cancel') || 'Fechar'}
          >
            {!isMobile && (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </button>
        </div>
      )}
    >
      <div className="space-y-4">
        <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 px-4 py-2.5 flex items-center gap-4">
          <div>
            <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{te('replicate.modal.info.amount')}</span>
            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">{new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(entry.amount)}</p>
          </div>
          {entry.party_name_snapshot && (
            <>
              <div className="w-px h-8 bg-emerald-200 dark:bg-emerald-700" />
              <div className="min-w-0">
                <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">{te('replicate.modal.info.contact')}</span>
                <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 truncate">{entry.party_name_snapshot}</p>
              </div>
            </>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{te('recurrence.modal.periodicity')}</label>
          <select
            value={periodicity}
            onChange={(e) => setPeriodicity(e.target.value as RecurrencePeriodicity)}
            disabled={submitting}
            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors"
          >
            <option value="daily">{te('recurrence.modal.periodicity_options.daily')}</option>
            <option value="weekly">{te('recurrence.modal.periodicity_options.weekly')}</option>
            <option value="monthly">{te('recurrence.modal.periodicity_options.monthly')}</option>
            <option value="yearly">{te('recurrence.modal.periodicity_options.yearly')}</option>
            <option value="installment">{te('recurrence.modal.periodicity_options.installment')}</option>
          </select>
        </div>

        {(periodicity === 'monthly' || periodicity === 'installment') && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Dia do mês para vencimento</label>
              <div className="flex gap-2 items-center">
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={monthlyTargetDay ?? ''}
                  onChange={(e) => {
                    const val = Number(e.target.value)
                    if (val >= 1 && val <= 31) setMonthlyTargetDay(val)
                  }}
                  disabled={submitting}
                  placeholder="Ex: 5"
                  className="w-20 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                <span className="text-xs text-slate-600 dark:text-slate-400">de cada mês</span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Em meses com menos dias (ex: fevereiro), será usado o último dia do mês</p>
            </div>

            {monthlyPreviewDates.length > 0 && (
              <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-3">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-300 mb-2">Próximas ocorrências:</p>
                <div className="space-y-1">
                  {monthlyPreviewDates.map((date, idx) => {
                    const d = new Date(date + 'T00:00:00')
                    const formatted = d.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })
                    return (
                      <p key={idx} className="text-xs text-blue-600 dark:text-blue-300">• {formatted}</p>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {periodicity === 'weekly' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{te('recurrence.modal.dayOfWeek')}</label>
            <div className="flex gap-1.5 flex-wrap">
              {(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const).map((label, dow) => (
                <button
                  key={dow}
                  type="button"
                  onClick={() => setWeeklyDow(dow)}
                  disabled={submitting}
                  className={`flex-1 min-w-[2.5rem] px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${weeklyDow === dow ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300 hover:border-emerald-400'}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        )}

        {periodicity === 'daily' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Dias da semana</label>
            <div className="flex gap-1.5 flex-wrap">
              {(['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const).map((label, dow) => (
                <button
                  key={dow}
                  type="button"
                  onClick={() => {
                    if (dailyWeekdays.includes(dow)) {
                      setDailyWeekdays(dailyWeekdays.filter(d => d !== dow))
                    } else {
                      setDailyWeekdays([...dailyWeekdays, dow].sort((a, b) => a - b))
                    }
                  }}
                  disabled={submitting}
                  className={`flex-1 min-w-[2.5rem] px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${dailyWeekdays.includes(dow) ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300 hover:border-emerald-400'}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">Selecione os dias em que a recorrência deve ocorrer</p>
          </div>
        )}

        {periodicity === 'installment' && (
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">{te('recurrence.modal.installmentCurrentTotal', { curr: String(installmentCurrent), total: String(installmentTotal) })}</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{te('recurrence.modal.installment.current') || 'Parcela atual'}</label>
                <input
                  type="number"
                  value={installmentCurrent}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === '' || val === '0') setInstallmentCurrent(1)
                    else {
                      const num = Number(val)
                      if (!isNaN(num)) {
                        const clamped = Math.max(1, Math.min(360, num))
                        if (clamped <= installmentTotal) setInstallmentCurrent(clamped)
                      }
                    }
                  }}
                  disabled={submitting}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{te('recurrence.modal.installment.total') || 'Total de parcelas'}</label>
                <input
                  type="number"
                  value={installmentTotal}
                  onChange={(e) => {
                    const val = e.target.value
                    if (val === '' || val === '0') setInstallmentTotal(installmentCurrent)
                    else {
                      const num = Number(val)
                      if (!isNaN(num)) {
                        const clamped = Math.max(installmentCurrent, Math.min(360, num))
                        setInstallmentTotal(clamped)
                      }
                    }
                  }}
                  disabled={submitting}
                  className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>
            </div>
          </div>
        )}

        {(periodicity === 'monthly' || periodicity === 'yearly' || periodicity === 'installment') && (
        <div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{te('recurrence.modal.dateAdjustments')}</span>
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`flex items-center gap-1 text-xs transition-colors ${showAdvanced ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {te('recurrence.modal.advanced')}
            </button>
          </div>

          {showAdvanced && (
            <div className="mt-3 space-y-4 p-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30">
              <div>
                <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">{te('recurrence.modal.weekendAdjust.label')}</p>
                <div className="flex flex-wrap gap-2">
                  {([{ value: 'none', label: te('recurrence.modal.weekendAdjust.none') }, { value: 'next', label: te('recurrence.modal.weekendAdjust.next') }, { value: 'prev', label: te('recurrence.modal.weekendAdjust.prev') }] as const).map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setWeekendAdjust(opt.value)}
                      disabled={submitting}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${weekendAdjust === opt.value ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300 hover:border-emerald-400'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={weekdayLock}
                    onChange={(e) => setWeekdayLock(e.target.checked)}
                    disabled={submitting}
                    className="rounded border-slate-300 dark:border-slate-500 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span className="text-xs font-medium text-slate-600 dark:text-slate-400">{te('recurrence.modal.weekdayLock')}</span>
                </label>
                {weekdayLock && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((label, dow) => (
                      <button
                        key={dow}
                        type="button"
                        onClick={() => setWeekdayLockDay(dow)}
                        disabled={submitting}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${weekdayLockDay === dow ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-500 text-slate-600 dark:text-slate-300 hover:border-emerald-400'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        )}

        {periodicity !== 'installment' && (
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {te('recurrence.modal.endDate') || 'Data de encerramento'}
            <span className="ml-1 text-xs font-normal text-slate-400 dark:text-slate-500">({te('recurrence.modal.endDateOptional') || 'opcional'})</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={endDate ?? ''}
              onChange={(e) => setEndDate(e.target.value || null)}
              disabled={submitting}
              className="flex-1 px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            {endDate && (
              <button
                type="button"
                onClick={() => setEndDate(null)}
                disabled={submitting}
                className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                title={te('recurrence.modal.endDateClear') || 'Limpar data de encerramento'}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>
        )}

        <p className="text-xs text-slate-500 dark:text-slate-400">{te('recurrence.modal.info')}</p>
      </div>
    </Modal>
  )
}
